import React, { useState, useEffect } from 'react'
import { MessageCircle, Send, Trash2, Edit2, Reply } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getImageComments, addComment, deleteComment, updateComment, Comment } from '../../lib/supabase'
import { supabase } from '../../lib/supabase'

interface CommentSectionProps {
  imageId: string
  isPublic: boolean
}

const CommentSection: React.FC<CommentSectionProps> = ({ imageId, isPublic }) => {
  const { user } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [editingComment, setEditingComment] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isPublic) {
      loadComments()
      // Check if database is set up
      checkDatabaseSetup()
    }
  }, [imageId, isPublic])

  const loadComments = async () => {
    try {
      setLoading(true)
      const commentsData = await getImageComments(imageId)
      setComments(commentsData)
    } catch (error) {
      console.error('Error loading comments:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkDatabaseSetup = async () => {
    try {
      // Try to query the comments table to see if it exists
      const { data, error } = await supabase.from('comments').select('id').limit(1)
      if (error) {
        console.warn('⚠️ Comments table may not exist:', error.message)
        console.warn('💡 Please run the database setup SQL to enable comments')
      } else {
        console.log('✅ Comments table is accessible')
      }
    } catch (error) {
      console.error('❌ Error checking database setup:', error)
    }
  }

  const handleAddComment = async () => {
    console.log('🔄 Attempting to add comment:', {
      newComment: newComment.trim(),
      user: user ? { id: user.id, email: user.email } : null,
      imageId,
      submitting
    })

    if (!newComment.trim()) {
      console.warn('❌ Cannot add comment: No content')
      alert('Please enter a comment before posting.')
      return
    }

    if (!user) {
      console.warn('❌ Cannot add comment: User not authenticated')
      alert('You must be signed in to post comments.')
      return
    }

    if (submitting) {
      console.warn('❌ Cannot add comment: Already submitting')
      return
    }

    try {
      setSubmitting(true)
      console.log('📤 Calling addComment function...')
      const comment = await addComment(imageId, newComment)
      console.log('✅ Comment added successfully:', comment)
      setComments(prev => [...prev, { ...comment, replies: [] }])
      setNewComment('')
      
      // Show success message
      console.log('🎉 Comment posted successfully!')
    } catch (error) {
      console.error('❌ Error adding comment:', error)
      
      // More specific error messages
      let errorMessage = 'Unknown error'
      if (error instanceof Error) {
        errorMessage = error.message
        
        // Check for specific error types
        if (errorMessage.includes('relation "comments" does not exist')) {
          errorMessage = 'Comments feature is not set up yet. Please contact support to enable commenting.'
        } else if (errorMessage.includes('permission denied')) {
          errorMessage = 'You do not have permission to comment on this image.'
        } else if (errorMessage.includes('authentication')) {
          errorMessage = 'Please sign in again to post comments.'
        } else if (errorMessage.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.'
        }
      }
      
      alert(`Failed to add comment: ${errorMessage}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddReply = async (parentId: string) => {
    if (!replyContent.trim() || !user || submitting) return

    try {
      setSubmitting(true)
      const reply = await addComment(imageId, replyContent, parentId)
      
      setComments(prev => prev.map(comment => 
        comment.id === parentId 
          ? { ...comment, replies: [...(comment.replies || []), reply] }
          : comment
      ))
      
      setReplyTo(null)
      setReplyContent('')
    } catch (error) {
      console.error('Error adding reply:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: string, isReply = false, parentId?: string) => {
    try {
      await deleteComment(commentId)
      
      if (isReply && parentId) {
        setComments(prev => prev.map(comment => 
          comment.id === parentId 
            ? { ...comment, replies: comment.replies?.filter(reply => reply.id !== commentId) || [] }
            : comment
        ))
      } else {
        setComments(prev => prev.filter(comment => comment.id !== commentId))
      }
    } catch (error) {
      console.error('Error deleting comment:', error)
    }
  }

  const handleEditComment = async (commentId: string) => {
    if (!editContent.trim() || submitting) return

    try {
      setSubmitting(true)
      const updatedComment = await updateComment(commentId, editContent)
      
      setComments(prev => prev.map(comment => {
        if (comment.id === commentId) {
          return { ...comment, content: updatedComment.content, updated_at: updatedComment.updated_at }
        }
        if (comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map(reply => 
              reply.id === commentId 
                ? { ...reply, content: updatedComment.content, updated_at: updatedComment.updated_at }
                : reply
            )
          }
        }
        return comment
      }))
      
      setEditingComment(null)
      setEditContent('')
    } catch (error) {
      console.error('Error editing comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const CommentItem = ({ comment, isReply = false, parentId }: { comment: Comment, isReply?: boolean, parentId?: string }) => (
    <div className={`${isReply ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''}`}>
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-sm font-medium">
            {comment.profiles?.display_name?.[0] || comment.profiles?.username?.[0] || '?'}
          </span>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className="font-medium text-gray-900 text-sm">
              {comment.profiles?.display_name || comment.profiles?.username || 'Anonymous'}
            </span>
            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
            {comment.updated_at !== comment.created_at && (
              <span className="text-xs text-gray-400">(edited)</span>
            )}
          </div>
          
          {editingComment === comment.id ? (
            <div className="space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                placeholder="Edit your comment..."
              />
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditComment(comment.id)}
                  disabled={submitting || !editContent.trim()}
                  className="px-3 py-1 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingComment(null)
                    setEditContent('')
                  }}
                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-gray-700 text-sm mb-2">{comment.content}</p>
              
              <div className="flex items-center space-x-3 text-xs text-gray-500">
                {!isReply && (
                  <button
                    onClick={() => {
                      setReplyTo(comment.id)
                      setReplyContent('')
                    }}
                    className="flex items-center space-x-1 hover:text-primary-600"
                  >
                    <Reply className="w-3 h-3" />
                    <span>Reply</span>
                  </button>
                )}
                
                {user && user.id === comment.user_id && (
                  <>
                    <button
                      onClick={() => {
                        setEditingComment(comment.id)
                        setEditContent(comment.content)
                      }}
                      className="flex items-center space-x-1 hover:text-blue-600"
                    >
                      <Edit2 className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteComment(comment.id, isReply, parentId)}
                      className="flex items-center space-x-1 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Reply form */}
      {replyTo === comment.id && (
        <div className="mt-3 ml-11">
          <div className="flex space-x-2">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
              placeholder="Write a reply..."
            />
            <div className="flex flex-col space-y-1">
              <button
                onClick={() => handleAddReply(comment.id)}
                disabled={submitting || !replyContent.trim()}
                className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setReplyTo(null)
                  setReplyContent('')
                }}
                className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {comment.replies.map(reply => (
            <CommentItem 
              key={reply.id} 
              comment={reply} 
              isReply={true} 
              parentId={comment.id}
            />
          ))}
        </div>
      )}
    </div>
  )

  if (!isPublic) {
    return null
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <MessageCircle className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">
          Comments ({comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0)})
        </h3>
      </div>
      
      {/* Add comment form */}
      {user ? (
        <div className="flex space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-medium">
              {user.email?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1 space-y-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              placeholder="Share your thoughts about this image..."
            />
            <div className="flex justify-end">
              <button
                onClick={handleAddComment}
                disabled={submitting || !newComment.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span>Comment</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          <p>Sign in to leave a comment</p>
        </div>
      )}
      
      {/* Comments list */}
      {loading ? (
        <div className="text-center py-4">
          <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto"></div>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-6">
          {comments.map(comment => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No comments yet</p>
          <p className="text-sm">Be the first to share your thoughts!</p>
        </div>
      )}
    </div>
  )
}

export default CommentSection 